import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame, Wine, Gem, PenTool, Mail } from "lucide-react";
import { IMG, CONTACT } from "../lib/site";
import { Reveal, Eyebrow, SectionHead, EmberField, GoldButton, GhostButton, EASE } from "../components/motion";
import { Ribbon } from "../components/Ribbon";

const HERO_LINES = ["Where Fire Meets", "Fine Craftsmanship"];

const JOURNEY = [
  { icon: Wine, title: "Choose the Barrel", copy: "Kentucky bourbon oak or a French wine cask — each carries its own history, grain and character.", img: IMG.cellar, testId: "journey-card-barrel" },
  { icon: Gem, title: "Crown It in Stone", copy: "Black granite, limestone, Hermosa, slate — precision water-jet cut to complement your property.", img: IMG.swatches, testId: "journey-card-stone" },
  { icon: Flame, title: "Set the Flame", copy: "A 65,000 BTU propane burner dressed in volcanic lava, reflective fireglass or river stone.", img: IMG.flame, testId: "journey-card-flame" },
  { icon: PenTool, title: "Make It Yours", copy: "A family name, an estate crest, a corporate emblem — burned into the staves by hand.", img: IMG.branding, testId: "journey-card-engraving" },
];

const COLLECTIONS = [
  { name: "The Belleville", desc: "Whiskey barrel · flamed black granite · custom engraving", price: "From $3,850", img: IMG.heroTable, testId: "collection-card-belleville" },
  { name: "The Vintner", desc: "French wine cask · honed limestone · round burner", price: "From $4,050", img: IMG.cellar, testId: "collection-card-vintner" },
  { name: "The Estate Crest", desc: "Bespoke crest edition · Hermosa stone · fireglass media", price: "From $4,900", img: IMG.branding, testId: "collection-card-estate" },
];

const TESTIMONIALS = [
  { quote: "Guests walk past the infinity pool and straight to the fire table. It has become the heart of the terrace.", name: "Boutique Winery, Niagara", testId: "testimonial-winery" },
  { quote: "Our family crest burned into the barrel — it isn't furniture, it's an heirloom we gather around every evening.", name: "Estate Owner, Muskoka", testId: "testimonial-estate" },
  { quote: "No plastic, no patio-store look. The stone, the oak, the flame — pure craftsmanship.", name: "Private Golf Club, Ontario", testId: "testimonial-golf" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const txtY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_75%_40%,rgba(217,119,6,0.14),transparent_70%)]" />
      <div className="animate-ember-pulse absolute right-[12%] top-[30%] h-[420px] w-[420px] rounded-full bg-amber-600/10 blur-[120px]" />
      <EmberField count={26} />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-24 pt-36 sm:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:pb-16">
        <motion.div style={{ y: txtY, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="hairline-gold w-10" />
            <Eyebrow>Handcrafted Outdoor Luxury · Est. 2024</Eyebrow>
          </motion.div>

          <h1 className="mt-7 font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
            {HERO_LINES.map((line, i) => (
              <span className="mask-line" key={line}>
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.15, delay: 0.35 + i * 0.16, ease: EASE }}
                >
                  {i === 1 ? <span className="text-gold-gradient italic">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg"
          >
            Authentic wine and whiskey barrels, transformed into bespoke luxury fire tables —
            crowned in natural stone and engraved with the name that matters to you. Not bought
            from a showroom floor. Created specifically for you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/configurator">
              <GoldButton data-testid="hero-cta-atelier">
                Design Your Table <ArrowRight size={15} />
              </GoldButton>
            </Link>
            <Link to="/contact">
              <GhostButton data-testid="hero-cta-commission">
                <Mail size={14} className="text-amber-500" /> Book a Private Commission
              </GhostButton>
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500"
          >
            Ontario Crafted · Serving Canada & the United States
          </motion.p>
        </motion.div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="animate-ember-pulse absolute -inset-6 rounded-[2rem] bg-amber-600/15 blur-3xl" />
          <div className="relative overflow-hidden rounded-[1.6rem] border border-amber-500/25 shadow-ember-lg">
            <img
              src={IMG.heroTable}
              alt="Cozy Ember whiskey barrel fire table with granite top, flames at dusk"
              className="aspect-[4/5] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-400">Signature Piece</p>
                <p className="mt-1 font-serif text-xl italic text-stone-100">The Belleville</p>
              </div>
              <p className="font-mono text-xs text-stone-300">from $3,850</p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-amber-500 to-transparent"
        />
      </motion.div>
    </section>
  );
};

const Story = () => (
  <section id="story" data-testid="story-section" className="relative py-28 sm:py-36">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-10 lg:grid-cols-2">
      <div>
        <SectionHead
          eyebrow="The Cozy Ember Experience"
          title={
            <>
              We are not selling a fire table.
              <span className="mt-2 block italic text-gold-gradient">We are creating the place people gather.</span>
            </>
          }
        />
        <Reveal delay={0.2}>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-stone-400 sm:text-lg font-light">
            <p>Where a glass of wine is enjoyed after dinner. Where friends stay outside a little longer on a cool Canadian evening. Where conversations continue long after the sun goes down.</p>
            <p>No mass-produced plastic appearance. No ordinary patio-store furniture. No disposable trend — only the warmth of fire, the character of natural materials, and the individuality of custom craftsmanship.</p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <blockquote className="mt-10 border-l-2 border-amber-500/60 pl-6 font-serif text-xl italic leading-relaxed text-stone-200 sm:text-2xl">
            "The greatest luxury isn't having what everyone else wants. It's having something created specifically for you."
          </blockquote>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="relative">
        <div className="animate-ember-pulse absolute -inset-5 rounded-[2rem] bg-amber-700/10 blur-3xl" />
        <div className="relative overflow-hidden rounded-[1.6rem] border border-stone-800">
          <img src={IMG.twilight} alt="Luxury patio at dusk around a fire" className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] hover:scale-105" />
          <div className="vignette absolute inset-0" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Journey = () => (
  <section data-testid="journey-section" className="relative border-t border-amber-900/20 bg-[#0c0a08] py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-10">
      <SectionHead
        eyebrow="The Commission Journey"
        title="Four choices. One unmistakable piece."
        copy="Every Cozy Ember begins as a conversation. You choose the character, the stone, the flame — and, if you wish, the name it carries."
      />
      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {JOURNEY.map((step, i) => (
          <Reveal key={step.testId} delay={i * 0.12}>
            <div
              data-testid={step.testId}
              className="group relative h-full overflow-hidden rounded-2xl border border-stone-800 bg-[#141110] transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-amber-500/40 hover:shadow-ember"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={step.img} alt={step.title} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141110] via-black/30 to-transparent" />
                <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 backdrop-blur-sm">
                  <step.icon size={16} className="text-amber-400" />
                </span>
                <span className="absolute right-4 top-4 font-mono text-[10px] tracking-[0.25em] text-amber-500/80">0{i + 1}</span>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-lg text-stone-100">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-400">{step.copy}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2} className="mt-12">
        <Link to="/configurator" data-testid="journey-cta-configurator" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors duration-200 hover:text-amber-300">
          Begin in the Bespoke Atelier <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const CollectionsPreview = () => (
  <section data-testid="collections-preview" className="py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead
          eyebrow="Signature Collections"
          title="Pieces people stop and ask about."
        />
        <Reveal delay={0.15}>
          <Link to="/collections" data-testid="collections-view-all" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors duration-200 hover:text-amber-300">
            View the Collection <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {COLLECTIONS.map((c, i) => (
          <Reveal key={c.testId} delay={i * 0.12}>
            <Link to="/collections" data-testid={c.testId} className="group block">
              <div className="relative overflow-hidden rounded-2xl border border-stone-800 transition-[border-color,box-shadow] duration-300 group-hover:border-amber-500/40 group-hover:shadow-ember">
                <img src={c.img} alt={c.name} className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-400">{c.price}</p>
                  <h3 className="mt-1 font-serif text-2xl text-stone-50">{c.name}</h3>
                  <p className="mt-2 text-sm text-stone-400">{c.desc}</p>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GalleryStrip = () => {
  const strip = [
    { img: IMG.flame, alt: "Flame over volcanic lava rock" },
    { img: IMG.heroTable, alt: "The Belleville fire table installed on a deck" },
    { img: IMG.branding, alt: "Custom crest burned into barrel oak" },
    { img: IMG.twilight, alt: "Lakeside evenings around the fire" },
  ];
  return (
    <section data-testid="gallery-strip" className="border-t border-amber-900/20 bg-[#0c0a08] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal><Eyebrow>The Lookbook</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-serif text-2xl tracking-tight text-stone-50 sm:text-3xl">
                Commissions, workshop details, and the evenings between.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/gallery" data-testid="gallery-strip-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors duration-200 hover:text-amber-300">
              View the Gallery <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {strip.map((s, i) => (
            <Reveal key={s.alt} delay={i * 0.08}>
              <Link to="/gallery" className="group block overflow-hidden rounded-xl border border-stone-800 transition-[border-color] duration-300 hover:border-amber-500/45">
                <img src={s.img} alt={s.alt} className="aspect-square w-full object-cover transition-transform duration-[1300ms] group-hover:scale-105" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const HospitalityTeaser = () => (
  <section data-testid="hospitality-teaser" className="relative overflow-hidden border-y border-amber-900/20">
    <img src={IMG.vineyard} alt="Luxury vineyard estate terrace at dusk" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#080706] via-[#080706]/88 to-[#080706]/40" />
    <div className="relative mx-auto max-w-7xl px-5 py-28 sm:px-10 sm:py-36">
      <div className="max-w-xl">
        <SectionHead
          eyebrow="Hospitality & Trade"
          title="Turn your brand into part of the guest experience."
          copy="Wineries, distilleries, resorts, golf clubs and restaurants — imagine a collection of fire tables bearing your identity, with guests gathering and photographing around them. Your brand, integrated into the evening itself."
        />
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/hospitality">
              <GoldButton data-testid="hospitality-cta-explore">Explore Partnerships <ArrowRight size={15} /></GoldButton>
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-10">
      <SectionHead eyebrow="Around the Fire" title="Evenings that became occasions." align="center" />
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.testId} delay={i * 0.12}>
            <figure data-testid={t.testId} className="flex h-full flex-col rounded-2xl border border-stone-800 bg-[#141110] p-8 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-amber-500/35">
              <span className="font-serif text-5xl leading-none text-amber-500/50">"</span>
              <blockquote className="mt-2 flex-1 font-serif text-lg italic leading-relaxed text-stone-200">{t.quote}</blockquote>
              <figcaption className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-amber-500/80">{t.name}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTABanner = () => (
  <section data-testid="cta-banner" className="relative overflow-hidden border-t border-amber-900/20 bg-[#0c0a08] py-24 sm:py-32">
    <EmberField count={16} />
    <div className="animate-ember-pulse absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-600/12 blur-[100px]" />
    <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-10">
      <Reveal>
        <Flame size={28} className="animate-flame mx-auto text-amber-500" />
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight text-stone-50 sm:text-4xl">
          Designed around your property.
          <span className="block italic text-gold-gradient">Built around your personality.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mx-auto mt-5 max-w-xl text-base text-stone-400 sm:text-lg">
          Begin your commission today — or write to our concierge at {CONTACT.email}.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Link to="/configurator">
            <GoldButton data-testid="cta-banner-atelier">Start Designing <ArrowRight size={15} /></GoldButton>
          </Link>
          <a href={CONTACT.emailHref}>
            <GhostButton data-testid="cta-banner-email"><Mail size={14} className="text-amber-500" /> {CONTACT.email}</GhostButton>
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Ribbon />
      <Story />
      <Journey />
      <CollectionsPreview />
      <GalleryStrip />
      <HospitalityTeaser />
      <Testimonials />
      <CTABanner />
    </>
  );
}
